import React, { useState } from 'react';
import {
    Button,
    Typography,
    Stack,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Divider,
    IconButton,
    useTheme,
    CircularProgress,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { ArrowBack } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import { APP_CONFIG } from '../../../../../config/app.config';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import { useWarehouses } from '../../../hooks/useWarehouseOperations';
import { useToast } from '@shared/hooks/useToast';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import ProductFlowCredentialsForm from '@features/connections/product_flow/components/ProductFlowCredentialsForm';
import WarehouseConnectionSelect from '../WarehouseConnectionSelect';
import { useProductFlowConnector, useProductFlowMarketplaceInventory, useProductFlowMarketplaces, useProductFlowSync, useProductFlowWarehouseInventory } from '@features/connections/product_flow/hooks/useProductFlow';
import { InventoryPreview } from '@features/warehouses/components/inventory-preview/InventoryPreview';

interface ProductFlowWarehouseFormProps {
    onClose: () => void;
    onBack: () => void;
}

const ProductFlowWarehouseForm: React.FC<ProductFlowWarehouseFormProps> = ({
    onClose,
    onBack,
}) => {
    const theme = useTheme();

    const [form, setForm] = useState({
        name: 'ProductFlow',
        location: '',
    });

    const queryClient = useQueryClient();
    const { error } = useToast();

    const [showCreateConnection, setShowCreateConnection] = useState(false);


    const handleCloseCreateConnection = () => {
        setShowCreateConnection(false);
    };

    const handleNewConnection = (marketplaceConnection: Connection) => {
        setShowCreateConnection(false);
        queryClient.invalidateQueries({ queryKey: ['connections'] });
        setSelectedConnection(marketplaceConnection);
    };

    const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
    const [selectedMarketplace, setSelectedMarketplace] = useState<string | null>(null);
    const [selectedMarketplaceType, setSelectedMarketplaceType] = useState<'warehouse' | 'marketplace' | null>(null);

    const { createWarehouse, isCreating } = useWarehouses();
    const { syncInventory, syncMarketplaceOffers } = useProductFlowSync();
    const [isSyncing, setIsSyncing] = useState(false);

    const isLoading = isCreating || isSyncing;

    const handleClose = () => {
        setForm({ name: '', location: '' });
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedConnection || (selectedMarketplaceType === 'marketplace' && !selectedMarketplace)) {
            error('Please select a warehouse or location');
            return;
        }

        const warehouse = {
            name: form.name,
            location: form.location,
            warehouseConnections: [
                {
                    sourceType: WarehouseConnectionType.PRODUCT_FLOW,
                    mutationType: MutationType.SYNC,
                    rules: [
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.CONNECTION_ID,
                            value: selectedConnection.id
                        },
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.SOURCE,
                            value: selectedMarketplaceType === 'warehouse' ? 'warehouse' : selectedMarketplace!
                        },
                    ],
                }
            ]
        }

        await createWarehouse(warehouse);

        try {
            setIsSyncing(true);
            if (selectedMarketplaceType === 'warehouse') {
                await syncInventory(selectedConnection.id);
            } else {
                await syncMarketplaceOffers(selectedConnection.id);
            }
        } finally {
            setIsSyncing(false);
        }

        handleClose();
    };

    const handleCreateConnection = () => {
        setShowCreateConnection(true);
    };

    const { connector } = useProductFlowConnector();
    const { marketplaces } = useProductFlowMarketplaces(selectedConnection?.id || '');
    const { warehouseInventory, isLoading: isLoadingWarehouseInventory } = useProductFlowWarehouseInventory(selectedConnection?.id || '');
    const { marketplaceInventory, isLoading: isLoadingMarketplaceInventory } = useProductFlowMarketplaceInventory(selectedConnection?.id || '', selectedMarketplace || '');

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center">
                        <IconButton onClick={onBack} color="inherit">
                            <ArrowBack />
                        </IconButton>

                        <Typography variant="h6" component="div">
                            Sync with ProductFlow
                        </Typography>
                    </Stack>
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ flex: 1 }}>
                <Stack gap={3} sx={{ mt: 2 }}>
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Box sx={{ width: 100, height: 60, borderRadius: 1, border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
                                <img src={connector?.image} alt="ProductFlow" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" color="text.primary">
                                    Sync your inventory with ProductFlow
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Your ProductFlow inventory will be synced with your Inventory every hour.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />


                    <Stack gap={2} direction="column">
                        <Typography variant="body2" color="text.secondary">
                            Warehouse details
                        </Typography>

                        <Stack gap={2} direction="row">
                            <TextField
                                label="Warehouse name"
                                value={form.name}
                                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                size="small"
                            />

                            <TextField
                                label="Location"
                                value={form.location}
                                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                                fullWidth
                                size="small"
                                placeholder="e.g., Amsterdam, Netherlands"
                            />
                        </Stack>
                    </Stack>


                    <WarehouseConnectionSelect
                        connectorType={ClientType.PRODUCT_FLOW}
                        selectedConnection={selectedConnection}
                        onSelect={setSelectedConnection}
                        onCreateConnection={handleCreateConnection}
                        connectorName="ProductFlow"
                    />

                    {selectedConnection && (
                        <Stack gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                Select what to sync
                            </Typography>

                            <Stack gap={2} direction="row" flexWrap="wrap">

                                <Button variant="outlined" onClick={() => setSelectedMarketplaceType('warehouse')} sx={{ borderColor: selectedMarketplaceType === 'warehouse' ? 'primary.main' : 'divider', borderWidth: 1, borderRadius: 1, padding: 2 }}>
                                    <Typography variant="subtitle2" color="text.primary">
                                        Warehouse Inventory
                                    </Typography>
                                </Button>


                                <Button variant="outlined" onClick={() => setSelectedMarketplaceType('marketplace')} sx={{ borderColor: selectedMarketplaceType === 'marketplace' ? 'primary.main' : 'divider', borderWidth: 1, borderRadius: 1, padding: 2 }}>
                                    <Typography variant="subtitle2" color="text.primary">
                                        Marketplace Inventory
                                    </Typography>
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    {selectedMarketplaceType === 'marketplace' && (
                        <Stack gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                Select a marketplace to sync with
                            </Typography>


                            <Stack gap={2} direction="row" flexWrap="wrap">

                                {marketplaces?.map(marketplace => (
                                    <Button key={marketplace.connection_id} variant="outlined" onClick={() => setSelectedMarketplace(marketplace.slug)} sx={{ borderColor: selectedMarketplace === marketplace.slug ? 'primary.main' : 'divider', borderWidth: 1, borderRadius: 1, padding: 2 }}>
                                        <Typography variant="subtitle2" color="text.primary">
                                            {marketplace.name}
                                        </Typography>
                                    </Button>
                                ))}
                            </Stack>
                        </Stack>
                    )}

                    {selectedMarketplaceType === 'warehouse' && (
                        <Stack gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                Warehouse Inventory
                            </Typography>

                            <InventoryPreview
                                inventory={warehouseInventory}
                                isLoading={isLoadingWarehouseInventory}
                                emptyMessage="No warehouse inventory available"
                                title="Preview of warehouse inventory"
                            />
                        </Stack>
                    )}

                    {selectedMarketplaceType === 'marketplace' && selectedMarketplace && (
                        <Stack gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                Marketplace Inventory
                            </Typography>

                            <InventoryPreview
                                inventory={marketplaceInventory}
                                isLoading={isLoadingMarketplaceInventory}
                                emptyMessage="No marketplace inventory available"
                                title={`Preview of ${marketplaces.find(m => m.slug === selectedMarketplace)?.name || 'marketplace'} inventory`}
                            />
                        </Stack>
                    )}

                </Stack>




            </DialogContent >

            <DialogActions sx={{ p: 3, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: theme.palette.divider }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!selectedConnection || isLoading}
                >
                    {isLoading ? <CircularProgress size={20} /> : 'Create warehouse'}
                </Button>
            </DialogActions>

            {/* Create ProductFlow Connection Dialog */}
            <Dialog
                open={showCreateConnection}
                onClose={handleCloseCreateConnection}
                maxWidth="sm"
            >
                <ProductFlowCredentialsForm
                    onNewConnection={handleNewConnection}
                    onClose={handleCloseCreateConnection}
                />
            </Dialog>



        </>
    );
};

export default ProductFlowWarehouseForm; 